import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BellOutlined,
  CloseOutlined,
  DeleteOutlined,
  LockOutlined,
  PlusOutlined,
  RightOutlined,
} from "@ant-design/icons";
import {
  Button,
  Input,
  message,
  Modal,
  Pagination,
  Segmented,
  Spin,
  Switch,
} from "antd";
import type { Language } from "../../types";
import {
  createRegulatoryMonitorTopic,
  deleteRegulatoryMonitorTopic,
  fetchRegulatoryMonitorTopics,
  fetchRegulatorySignals,
  fetchRegulatorySubscription,
  putRegulatorySubscription,
  type MonitorTopicDto,
  type RegulatorySignalDto,
} from "../../services/compliance";
import { useUiState } from "../../state";
import { textMap } from "../../i18n";
import { downloadJson } from "../../utils/exportFile";
import "../../styles/studio-monitoring.css";
import { STUDIO_MONITORING_EXPORT_EVENT } from "./studioConstants";

const SIGNALS_PAGE_SIZE = 3;

type SignalSourceTab = "ALL" | "FDA" | "FTC";

function parseTopicSlugs(raw: string[], catalogIds: Set<string>): string[] {
  const hit = raw
    .map((s) => s.trim().toLowerCase())
    .filter((s) => s && catalogIds.has(s));
  if (hit.length) {
    return [...new Set(hit)];
  }
  return Array.from(catalogIds);
}

function labelForTopic(item: MonitorTopicDto | undefined, lang: Language): string {
  if (!item) {
    return "—";
  }
  return lang === "zh" ? item.label_zh : item.label_en;
}

function formatSignalTime(iso: string | null, lang: Language): string {
  if (!iso) {
    return "—";
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return "—";
  }
  return new Intl.DateTimeFormat(lang === "zh" ? "zh-CN" : "en-US", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(d);
}

function sourcePillClass(source: string): "fda" | "ftc" | "rss" {
  const u = source.toUpperCase();
  if (u === "FDA") {
    return "fda";
  }
  if (u === "FTC") {
    return "ftc";
  }
  return "rss";
}

function sourcePillLabel(source: string, t: (typeof textMap)["en"]): string {
  const u = source.toUpperCase();
  if (u === "FDA") {
    return t.studioMonSourceFda;
  }
  if (u === "FTC") {
    return t.studioMonSourceFtc;
  }
  return t.studioMonSourceRss;
}

function tierToStatus(
  tier: string | null,
  t: (typeof textMap)["en"],
): { label: string; variant: "match" | "neutral" } {
  switch (tier) {
    case "high_match":
      return { label: t.studioMonStatusHighMatch, variant: "match" };
    case "direct_signal":
      return { label: t.studioMonStatusDirect, variant: "neutral" };
    default:
      return { label: t.studioMonStatusInfo, variant: "neutral" };
  }
}

function buildAnalyzeDraft(sig: RegulatorySignalDto, lang: Language): string {
  if (lang === "zh") {
    return `请帮我解读以下监管信号对我产品合规的潜在影响，并给出可执行的整改建议：\n\n标题：${sig.title}\n摘要：${sig.summary}\n链接：${sig.link}`;
  }
  return `Please assess the compliance impact of this regulatory signal and suggest concrete remediation steps:\n\nTitle: ${sig.title}\nSummary: ${sig.summary}\nLink: ${sig.link}`;
}

export function StudioMonitoringPage() {
  const { lang } = useUiState();
  const t = textMap[lang];
  const navigate = useNavigate();
  const [alertsOn, setAlertsOn] = useState(true);
  const [frequency, setFrequency] = useState<"realtime" | "digest">("realtime");
  const [catalog, setCatalog] = useState<MonitorTopicDto[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [topicIds, setTopicIds] = useState<string[]>([]);
  const [notificationEmail, setNotificationEmail] = useState("");
  const [page, setPage] = useState(1);
  const [signalSource, setSignalSource] = useState<SignalSourceTab>("ALL");
  const [signalSearchDraft, setSignalSearchDraft] = useState("");
  const [signalSearchApplied, setSignalSearchApplied] = useState("");
  const [signals, setSignals] = useState<RegulatorySignalDto[]>([]);
  const [signalsTotal, setSignalsTotal] = useState(0);
  const [signalsLoading, setSignalsLoading] = useState(true);
  const [prefsLoading, setPrefsLoading] = useState(true);
  const [pickTopicOpen, setPickTopicOpen] = useState(false);
  const [customTopicOpen, setCustomTopicOpen] = useState(false);
  const [customZh, setCustomZh] = useState("");
  const [customEn, setCustomEn] = useState("");
  const [customKw, setCustomKw] = useState("");
  const [customSubmitting, setCustomSubmitting] = useState(false);
  const prefsHydratedRef = useRef(false);

  useEffect(() => {
    prefsHydratedRef.current = false;
  }, [lang]);

  useEffect(() => {
    let cancelled = false;
    setCatalogLoading(true);
    (async () => {
      try {
        const res = await fetchRegulatoryMonitorTopics();
        if (cancelled) {
          return;
        }
        setCatalog(res?.items ?? []);
      } catch {
        if (!cancelled) {
          message.error(t.studioMonTopicsLoadFailed);
          setCatalog([]);
        }
      } finally {
        if (!cancelled) {
          setCatalogLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [lang, t.studioMonTopicsLoadFailed]);

  useEffect(() => {
    if (!catalog.length) {
      if (!catalogLoading) {
        setPrefsLoading(false);
      }
      return;
    }
    if (prefsHydratedRef.current) {
      return;
    }
    let cancelled = false;
    setPrefsLoading(true);
    (async () => {
      try {
        const sub = await fetchRegulatorySubscription();
        if (cancelled) {
          return;
        }
        if (!sub) {
          return;
        }
        const ids = new Set(catalog.map((c) => c.id));
        setAlertsOn(sub.enabled);
        setFrequency(sub.frequency === "realtime" ? "realtime" : "digest");
        setTopicIds(parseTopicSlugs(sub.topics, ids));
        setNotificationEmail(sub.notification_email ?? "");
        prefsHydratedRef.current = true;
      } catch {
        if (!cancelled) {
          message.error(textMap[lang].studioMonSubscriptionLoadFailed);
        }
      } finally {
        if (!cancelled) {
          setPrefsLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [lang, catalog, catalogLoading]);

  useEffect(() => {
    setPage(1);
  }, [topicIds.join(","), signalSource, signalSearchApplied]);

  useEffect(() => {
    let cancelled = false;
    setSignalsLoading(true);
    (async () => {
      try {
        const res = await fetchRegulatorySignals({
          page,
          pageSize: SIGNALS_PAGE_SIZE,
          source: signalSource,
          monitorTopics: topicIds,
          search: signalSearchApplied || undefined,
          lang,
        });
        if (cancelled) {
          return;
        }
        if (!res) {
          setSignals([]);
          setSignalsTotal(0);
          return;
        }
        setSignalsTotal(res.total);
        setSignals(res.items);
      } catch {
        if (!cancelled) {
          setSignals([]);
          setSignalsTotal(0);
          message.error(textMap[lang].studioMonSignalsLoadFailed);
        }
      } finally {
        if (!cancelled) {
          setSignalsLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [page, lang, topicIds, signalSource, signalSearchApplied]);

  useEffect(() => {
    const onExport = () => {
      if (!signals.length) {
        message.info(t.studioMonSignalsEmpty);
        return;
      }
      downloadJson(
        `xtool-regulatory-signals-${new Date().toISOString().slice(0, 10)}.json`,
        {
          exportedAt: new Date().toISOString(),
          page,
          pageSize: SIGNALS_PAGE_SIZE,
          total: signalsTotal,
          items: signals,
        },
      );
      message.success(t.studioMonExportReportOk);
    };
    window.addEventListener(STUDIO_MONITORING_EXPORT_EVENT, onExport);
    return () => window.removeEventListener(STUDIO_MONITORING_EXPORT_EVENT, onExport);
  }, [signals, signalsTotal, page, t]);

  const testNotify = async () => {
    if (typeof window === "undefined" || !("Notification" in window)) {
      message.info(t.studioMonTestNotifyUnsupported);
      return;
    }
    let perm = Notification.permission;
    if (perm === "default") {
      perm = await Notification.requestPermission();
    }
    if (perm !== "granted") {
      message.warning(t.studioMonTestNotifyDenied);
      return;
    }
    new Notification(
      lang === "zh" ? "XTool 监管提醒（测试）" : "XTool regulatory alert (test)",
      {
        body:
          lang === "zh"
            ? "这是一条浏览器测试通知。"
            : "This is a browser test notification.",
      },
    );
    message.success(t.studioMonTestNotifySent);
  };

  const catalogIdSet = () => new Set(catalog.map((c) => c.id));

  const topicMeta = (id: string) => catalog.find((c) => c.id === id);

  const savePrefs = async () => {
    try {
      const updated = await putRegulatorySubscription({
        enabled: alertsOn,
        frequency: frequency === "realtime" ? "realtime" : "daily",
        topics: topicIds,
      });
      setAlertsOn(updated.enabled);
      setFrequency(updated.frequency === "realtime" ? "realtime" : "digest");
      setTopicIds(parseTopicSlugs(updated.topics, catalogIdSet()));
      setNotificationEmail(updated.notification_email ?? "");
      message.success(t.studioMonPrefsSaved);
    } catch {
      message.error(t.studioMonPrefsSaveFailed);
    }
  };

  const refreshCatalog = async () => {
    try {
      const res = await fetchRegulatoryMonitorTopics();
      setCatalog(res?.items ?? []);
    } catch {
      message.error(t.studioMonTopicsLoadFailed);
    }
  };

  const removeTopic = (id: string) => {
    setTopicIds((prev) => prev.filter((x) => x !== id));
  };

  const openAddTopic = () => {
    const addable = catalog.filter((c) => !topicIds.includes(c.id));
    if (!addable.length) {
      message.info(t.studioMonPickTopicEmpty);
      return;
    }
    if (topicIds.length >= 12) {
      message.info(t.studioMonTopicsMax);
      return;
    }
    setPickTopicOpen(true);
  };

  const pickTopicAdd = (id: string) => {
    if (topicIds.length >= 12 || topicIds.includes(id)) {
      return;
    }
    setTopicIds((prev) => [...prev, id]);
    setPickTopicOpen(false);
  };

  const submitCustomTopic = async () => {
    const zh = customZh.trim();
    const en = customEn.trim();
    const rawKw = customKw
      .split(/[\n,，;；]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (!zh || !en || !rawKw.length) {
      message.warning(t.studioMonCustomKeywordsPh);
      return;
    }
    setCustomSubmitting(true);
    try {
      const res = await createRegulatoryMonitorTopic({
        label_zh: zh,
        label_en: en,
        keywords: rawKw,
      });
      if (res?.items) {
        setCatalog(res.items);
      }
      const slug = res?.created_slug?.trim();
      if (slug && !topicIds.includes(slug)) {
        setTopicIds((prev) => [...prev, slug]);
      }
      setCustomTopicOpen(false);
      setCustomZh("");
      setCustomEn("");
      setCustomKw("");
      message.success(t.studioMonCustomCreated);
    } catch {
      message.error(t.studioMonPrefsSaveFailed);
    } finally {
      setCustomSubmitting(false);
    }
  };

  const removeUserTopic = async (slug: string) => {
    const meta = topicMeta(slug);
    if (meta?.source !== "user") {
      return;
    }
    try {
      const ok = await deleteRegulatoryMonitorTopic(slug);
      if (!ok) {
        message.error(t.studioMonPrefsSaveFailed);
        return;
      }
      removeTopic(slug);
      await refreshCatalog();
      message.success(t.studioMonUserTopicDeleted);
    } catch {
      message.error(t.studioMonPrefsSaveFailed);
    }
  };

  return (
    <div className="studio-mon-root">
      <div className="studio-mon-grid">
        <section className="studio-mon-card">
          <h2 className="studio-mon-card-title">{t.studioMonPrefsTitle}</h2>
          <p className="studio-mon-card-lead">{t.studioMonPrefsLead}</p>

          <Spin spinning={prefsLoading || catalogLoading}>
            <div className="studio-mon-row-between">
              <div>
                <span className="studio-mon-field-label">
                  {t.studioMonMasterLabel}
                </span>
                <div className="studio-mon-field-hint">
                  {t.studioMonMasterHint}
                </div>
              </div>
              <Switch checked={alertsOn} onChange={setAlertsOn} />
            </div>

            <div style={{ marginTop: 20 }}>
              <label className="studio-mon-field-label">
                {t.studioMonEmailLabel}
              </label>
              <Input
                className="studio-mon-email"
                readOnly
                value={notificationEmail || "—"}
                suffix={
                  <LockOutlined style={{ color: "var(--wb-secondary)" }} />
                }
                style={{ marginTop: 8, borderRadius: 14 }}
              />
            </div>

            <div style={{ marginTop: 20 }}>
              <label className="studio-mon-field-label">
                {t.studioMonFreqLabel}
              </label>
              <Segmented
                className="studio-mon-seg"
                block
                style={{ marginTop: 8 }}
                value={frequency}
                onChange={(v) => setFrequency(v as "realtime" | "digest")}
                options={[
                  { label: t.studioMonFreqRealtime, value: "realtime" },
                  { label: t.studioMonFreqDigest, value: "digest" },
                ]}
              />
            </div>

            <div style={{ marginTop: 24 }}>
              <div className="studio-mon-topics-head">
                <label
                  className="studio-mon-field-label"
                  style={{ marginBottom: 0 }}
                >
                  {t.studioMonTopicsLabel}
                </label>
                <span className="studio-mon-topics-max">
                  {t.studioMonTopicsMax}
                </span>
              </div>
              <div className="studio-mon-tags">
                {topicIds.map((id) => {
                  const meta = topicMeta(id);
                  const userMade = meta?.source === "user";
                  return (
                    <span key={id} className="studio-mon-tag-pill">
                      {labelForTopic(meta, lang)}
                      {userMade ? (
                        <button
                          type="button"
                          className="studio-mon-tag-remove"
                          aria-label="delete custom topic"
                          onClick={() => void removeUserTopic(id)}
                        >
                          <DeleteOutlined style={{ fontSize: 10 }} />
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="studio-mon-tag-remove"
                        aria-label="remove"
                        onClick={() => removeTopic(id)}
                      >
                        <CloseOutlined style={{ fontSize: 10 }} />
                      </button>
                    </span>
                  );
                })}
                <button
                  type="button"
                  className="studio-mon-add-topic"
                  onClick={openAddTopic}
                  disabled={topicIds.length >= 12}
                >
                  <PlusOutlined />
                  {t.studioMonAddTopic}
                </button>
                <button
                  type="button"
                  className="studio-mon-add-topic"
                  onClick={() => setCustomTopicOpen(true)}
                  disabled={topicIds.length >= 12}
                >
                  <PlusOutlined />
                  {t.studioMonAddCustomTopic}
                </button>
              </div>
              <p className="studio-mon-field-hint" style={{ marginTop: 10 }}>
                {t.studioMonTopicsHint}
              </p>
            </div>
          </Spin>

          <div className="studio-mon-divider">
            <Button
              type="primary"
              block
              className="studio-mon-save-btn"
              loading={prefsLoading}
              onClick={savePrefs}
            >
              {t.studioMonSavePrefs}
            </Button>
          </div>
        </section>

        <section
          className="studio-mon-col-right"
          aria-label={t.studioMonSignalsTitle}
        >
          <div className="studio-mon-signals-head">
            <div>
              <h2 className="studio-mon-signals-title">
                {t.studioMonSignalsTitle}
              </h2>
              <p className="studio-mon-signals-sub">
                {t.studioMonSignalsSubtitle}
              </p>
              <p
                className="studio-mon-field-hint"
                style={{ marginTop: 8, maxWidth: 640 }}
              >
                {t.studioMonSignalsExplain}
              </p>
            </div>
            <button
              type="button"
              className="studio-mon-test-btn"
              onClick={() => void testNotify()}
            >
              <BellOutlined />
              {t.studioMonTestNotify}
            </button>
          </div>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 12,
              alignItems: "center",
              marginBottom: 12,
            }}
          >
            <Segmented<SignalSourceTab>
              value={signalSource}
              onChange={(v) => setSignalSource(v)}
              options={[
                { label: t.studioMonSourceAll, value: "ALL" },
                { label: t.studioMonSourceFda, value: "FDA" },
                { label: t.studioMonSourceFtc, value: "FTC" },
              ]}
            />
            <Input.Search
              allowClear
              placeholder={t.studioMonKeywordPlaceholder}
              enterButton={t.studioMonApplySearch}
              value={signalSearchDraft}
              onChange={(e) => setSignalSearchDraft(e.target.value)}
              onSearch={(v) => setSignalSearchApplied(v.trim())}
              style={{ minWidth: 220, maxWidth: 360, flex: "1 1 220px" }}
            />
          </div>

          <div className="studio-mon-signals-scroll">
            <Spin spinning={signalsLoading}>
              <div className="studio-mon-feed">
                {!signalsLoading && signals.length === 0 ? (
                  <p
                    className="studio-mon-field-hint"
                    style={{ margin: "12px 0" }}
                  >
                    {t.studioMonSignalsEmpty}
                  </p>
                ) : null}
                {signals.map((sig) => {
                  const status = tierToStatus(sig.signal_tier, t);
                  const srcClass = sourcePillClass(sig.source);
                  return (
                    <article key={sig.id} className="studio-mon-signal">
                      <div className="studio-mon-signal-top">
                        <div className="studio-mon-signal-meta">
                          <span
                            className={`studio-mon-src studio-mon-src-${srcClass}`}
                          >
                            {sourcePillLabel(sig.source, t)}
                          </span>
                          <span className="studio-mon-signal-time">
                            {formatSignalTime(sig.published_at, lang)}
                          </span>
                        </div>
                        <span
                          className={`studio-mon-status ${
                            status.variant === "match"
                              ? "studio-mon-status-match"
                              : "studio-mon-status-neutral"
                          }`}
                        >
                          {status.label}
                        </span>
                      </div>
                      <h3 className="studio-mon-signal-title">
                        <a
                          href={sig.link}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {sig.title}
                        </a>
                      </h3>
                      <p className="studio-mon-signal-desc">{sig.summary}</p>
                      <div className="studio-mon-signal-foot">
                        <div className="studio-mon-tags">
                          {sig.tags.map((tag, ti) => (
                            <span
                              key={`${sig.id}-tag-${ti}`}
                              className="studio-mon-chip"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                        <button
                          type="button"
                          className="studio-mon-analyze"
                          onClick={() =>
                            navigate("/chat", {
                              state: { studioDraft: buildAnalyzeDraft(sig, lang) },
                            })
                          }
                        >
                          {t.studioMonAnalyzeImpact}
                          <RightOutlined style={{ fontSize: 11 }} />
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>
            </Spin>
          </div>

          <div className="studio-mon-paginate">
            <Pagination
              size="small"
              current={page}
              total={signalsTotal}
              pageSize={SIGNALS_PAGE_SIZE}
              onChange={setPage}
              showSizeChanger={false}
              hideOnSinglePage
            />
          </div>
        </section>
      </div>

      <Modal
        title={t.studioMonPickTopicModalTitle}
        open={pickTopicOpen}
        onCancel={() => setPickTopicOpen(false)}
        footer={null}
        destroyOnClose
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {catalog
            .filter((c) => !topicIds.includes(c.id))
            .map((c) => (
              <Button
                key={c.id}
                block
                type="default"
                onClick={() => pickTopicAdd(c.id)}
              >
                {labelForTopic(c, lang)}
              </Button>
            ))}
        </div>
      </Modal>

      <Modal
        title={t.studioMonCustomTopicModalTitle}
        open={customTopicOpen}
        onCancel={() => setCustomTopicOpen(false)}
        footer={null}
        destroyOnClose
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div>
            <label className="studio-mon-field-label">{t.studioMonCustomLabelZh}</label>
            <Input
              value={customZh}
              onChange={(e) => setCustomZh(e.target.value)}
              style={{ marginTop: 6 }}
            />
          </div>
          <div>
            <label className="studio-mon-field-label">{t.studioMonCustomLabelEn}</label>
            <Input
              value={customEn}
              onChange={(e) => setCustomEn(e.target.value)}
              style={{ marginTop: 6 }}
            />
          </div>
          <div>
            <label className="studio-mon-field-label">{t.studioMonCustomKeywords}</label>
            <Input.TextArea
              value={customKw}
              onChange={(e) => setCustomKw(e.target.value)}
              rows={4}
              placeholder={t.studioMonCustomKeywordsPh}
              style={{ marginTop: 6 }}
            />
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Button onClick={() => setCustomTopicOpen(false)}>
              {t.studioMonCustomCancel}
            </Button>
            <Button
              type="primary"
              loading={customSubmitting}
              onClick={() => void submitCustomTopic()}
            >
              {t.studioMonCustomSubmit}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
